import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import {
  Grid,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  useMediaQuery,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material';
import { useAccount } from '../../contexts/Account';
import { submitAOBFStep2 } from '../../services/advisor_services';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../components/ACSnackbar';
import AdvisoryCloudLogo from '../../components/AdvisoryCloudLogo';
import RatingCard from './RatingCard';
import ChipList from '../../components/Advisor/Profile/components/chipList';

const useStyles = (isSM) => ({
  main: {
    with: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    padding: isSM ? '16px 8px' : '16px 120px 0',
    justifyContent: 'center',
    width: '100%',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '600px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: { xxs: '32px', xs: '24px' },
    letterSpacing: '-0.4300000071525574px',
    marginBottom: '16px',
    padding: { xxs: '0px 8px', xs: '0px' },
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    maxWidth: '360px',
    textAlign: 'center',
  },
  info: {
    fontStyle: 'italic',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '16px',
  },
  gridContainerProps: {
    container: true,
    spacing: 0,
    justifyContent: 'center',
  },
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
  label: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '8px',
  },
  textField: {
    marginBottom: '8px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        borderColor: '#C7D0DE',
      },
    },
  },
});

function AOBFStep2() {
  const { accountDetails, tags } = useAccount();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = useStyles(isSM);
  const history = useHistory();
  const [profession, setProfession] = useState(
    accountDetails.aobfPrimaryExpertise || ''
  );
  const [skills, setSkills] = useState(
    accountDetails.skillTagIds && Array.isArray(accountDetails.skillTagIds)
      ? accountDetails.skillTagIds.map((skId) =>
          tags.skills.find((x) => x.id == skId)
        )
      : []
  );

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const disableButton = () => isLoading || !profession;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      await submitAOBFStep2({
        id: accountDetails.id,
        aobfPrimaryExpertise: profession,
        skillTagIds: skills.map((x) => x.id),
      });

      history.push('/advisor/aobf/step3');
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      setOpenSnackbar(true);
    }
  };

  return (
    <>
      <Helmet>
        <title>Profile Builder - AdvisoryCloud</title>
      </Helmet>

      <Box sx={classes.main}>
        <Box sx={classes.header}>
          <AdvisoryCloudLogo
            removeHyperlink
            onClick={() => {}}
            width={isSM ? 240 : 180}
            height={isSM ? 80 : 60}
            cursor="default"
          />
        </Box>

        <Box sx={classes.body}>
          <Typography sx={classes.title}>Your Professional Brand</Typography>
          <Typography sx={{ ...classes.subtitle, maxWidth: '480px' }}>
            AdvisoryCloud removes the noise of other professional platforms,
            matching you only with senior-level opportunities. Tell us about
            your unique expertise so we can help you grow and monetize your
            professional brand.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <Typography sx={classes.label}>
                  Primary Area of Expertise:
                </Typography>
              </Grid>
              <Grid {...classes.gridItemProps}>
                <Select
                  fullWidth
                  value={profession}
                  onChange={(e) => setProfession(e.target.value)}
                  displayEmpty
                  sx={{ marginBottom: '8px', borderRadius: '10px' }}
                  id="aobf-profession"
                >
                  <MenuItem value="" disabled>
                    Select one
                  </MenuItem>
                  {[
                    'Founder or CEO',
                    'Marketing',
                    'Business Professional',
                    'Operations',
                    'Technology',
                    'Product',
                    'Finance',
                    'Sales',
                    'Human Resources',
                    'Legal',
                    'Other',
                  ].map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <Typography sx={classes.label}>
                  Select Any Additional Areas of Expertise (optional):
                </Typography>
                <Autocomplete
                  multiple
                  options={tags.skills}
                  value={skills}
                  onChange={(_, vals) => {
                    // if (vals.length <= 3) {
                    setSkills(vals);
                    // }
                  }}
                  disableCloseOnSelect
                  getOptionLabel={(opt) => opt.label}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  renderTags={(value, getTagProps) => (
                    <Chip
                      variant="filled"
                      label={
                        value.length +
                        (value.length === 1
                          ? ' Skill selected'
                          : ' Skills selected')
                      }
                      style={{
                        margin: '0',
                        padding: '0',
                        fontSize: '13px',
                        fontWeight: 500,
                        lineHeight: '24px',
                        letterSpacing: '-0.4300000071525574px',
                        color: '#646D7A',
                        backgroundColor: '#E1E9F6',
                      }}
                    />
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Start typing to search..."
                      variant="outlined"
                      sx={classes.textField}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={disableButton()}
                  id="aobf-submit-button"
                  sx={{
                    borderRadius: '50px',
                    backgroundColor: '#3171F6',
                    '&:hover': { backgroundColor: '#3171F6' },
                    height: '48px',
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 700,
                    mb: '16px',
                    mt: '8px',
                  }}
                >
                  {isLoading ? <LoadingLogoSkeleton /> : 'Continue'}
                </Button>
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <RatingCard
                  quote="I have been successfully matched by AdvisoryCloud with two companies that would never have been on my radar."
                  name="Michael Harris"
                  title="CEO, Michael Harris Group"
                />
              </Grid>
            </Grid>
            <ACSnackbar
              onClose={() => setOpenSnackbar(false)}
              open={openSnackbar}
              severity="error"
              text="Something went wrong"
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default AOBFStep2;
